import { ARN } from '@aws-sdk/util-arn-parser';
import React, { useContext, useEffect, useState } from 'react';
import { OperatorInfo } from 'src/components/OperatorInfo';
import { DecisionTable } from 'src/components/DecisionTable';
import { apiGetRequest } from 'src/utils/mlPigeonAPIRequest';
import { StatusLabel } from 'src/components/StatusLabel';
import { Link } from 'react-router-dom';
import { UserContext } from 'src/context/userContext';
import { OperatorModalContext } from 'src/context/operatorModalContext';
import {
  OperatorAction,
  OperatorActionMenu,
} from 'src/components/OperatorActionsMenu';
import { Box } from '@cloudscape-design/components';

export interface ApprovalOperatorNodeProps {
  header: string;
  info: {
    id: string;
    operatorName: string;
    operatorFlowId: string;
    modelName?: string;
    modelArn?: ARN;
  };
  status: OperatorJobStatus;
}

export const ApprovalOperatorNode = ({
  header,
  info,
  status,
}: ApprovalOperatorNodeProps) => {
  const user = useContext(UserContext);

  const { setApprovalConfig, setOverrideConfig } =
    useContext(OperatorModalContext);
  const [statusLoading, setStatusLoading] = useState<boolean>(false);
  const [decisions, setDecisions] = useState<ApprovalStatus[]>([]);
  const [requiredApprovals, setRequiredApprovals] = useState(null);
  const [isValidApprover, setIsValidApprover] = useState(true);

  useEffect(() => {
    if (!info.id || ['NOTSTARTED'].includes(status)) {
      return;
    }

    setStatusLoading(true);
    apiGetRequest({
      endpoint: `approvalOperators/${info.id}`,
    })
      .then((res) => {
        setRequiredApprovals(res.requiredApprovalCount);
        setDecisions(res.approvalStatuses);

        const userHasDecided =
          res.approvalStatuses?.filter((a: any) => a.createdBy === user.name)
            .length > 0;

        if (userHasDecided) {
          setIsValidApprover(false);
        }
      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        setStatusLoading(false);
      });
  }, [status, info.id]);

  const operatorActions: OperatorAction[] = [];

  operatorActions.push({
    name: 'Approve or Reject',
    description: 'manually approve operators',
    disabled: !isValidApprover || status !== 'PENDING',
    disabledReason:
      status !== 'PENDING'
        ? 'Approvals are only allowed when the status is Pending.'
        : !isValidApprover
          ? 'You either already responded or do not have permission to approve.'
          : undefined,
    requiredPermission: 'READ_AND_LIMITED_ACTION',
    actionFn: () => {
      setApprovalConfig({
        flowId: info.operatorFlowId,
        operatorName: info.operatorName,
        statusMessage: '',
        decision: '',
      });
    },
  });

  if (status === 'FAILED' || status === 'PENDING') {
    operatorActions.push({
      name: 'Override',
      description: 'override operators',
      requiredPermission: 'ADMINISTER',
      actionFn: () => {
        setOverrideConfig({
          id: info.operatorFlowId,
          action: 'SKIPPEDOVERRIDE',
          operatorName: info.operatorName,
          operatorStatus: status,
        });
      },
    });
  }

  return (
    <div className="job-visualization-node w-[730px]">
      <div className="job-visualization-node__header">
        <h3>{header}</h3>
        <div className="job-visualization-node__status-container">
          <StatusLabel
            loading={false}
            label={status}
            overrides={{
              SUCCEEDED: { label: 'Approved', type: 'success' },
              FAILED: { label: 'Rejected', type: 'error' },
            }}
          />
        </div>
      </div>
      <OperatorInfo operatorId={info.id} operatorName={info.operatorName} />
      <div className="approval-node__status-message">
        <Box margin={{ top: 'm', bottom: 'm' }}>
          <DecisionTable loading={statusLoading} decisions={decisions} />
        </Box>
      </div>

      <div className="job-visualization-node__buttons">
        {requiredApprovals && (
          <span className="mr-4">
            <span className="font-bold">
              {`${
                decisions?.filter((d) => d.status === 'SUCCEEDED').length ?? '0'
              } / ${requiredApprovals}`}{' '}
            </span>
            required approvals
          </span>
        )}
        <Link
          to={`../logs?operatorName=${info.operatorName}`}
          relative="path"
          target="_blank"
        >
          View Logs
        </Link>

        <OperatorActionMenu actions={operatorActions} />
      </div>
    </div>
  );
};
